import { z } from "zod";
import { PointGeometry } from "./geo";

// Active fire detections from NASA FIRMS (VIIRS), issue #307, spike
// docs/research/spik-pozary-z-satelity-2026-10-15.md. Satellite-detected hotspots for the HQ map —
// a WARNING/orientation layer, NOT tactical live: ~2-3 h latency for Europe, ~375 m pixel, clouds
// block it (no detection does NOT mean no fire). The panel shows a disclaimer and never calls off a
// search on this basis ("pomoc, nie wyrocznia"). The backend fetches through a proxy (lib/firms.ts)
// so the secret MAP_KEY never reaches the browser.

/** Minutes a FIRMS tile is served from cache before we ask the source again (source latency is
 * 2-3 h, so fresher is pointless; also keeps us far under the 5000 tx / 10 min FIRMS limit). */
export const FIRES_CACHE_MIN = 30;
export const FIRES_ATTRIBUTION = "Aktywne pożary: NASA FIRMS (VIIRS) — detekcja satelitarna";
/** Below this map zoom fires are not drawn (too coarse to place). */
export const FIRES_MIN_ZOOM = 6;

/** One satellite hotspot. */
export const FireDetection = z.object({
  type: z.literal("Feature"),
  geometry: PointGeometry,
  properties: z.object({
    /** Stable id: satellite + rounded lat/lon + acquisition time (dedup across sources/tiles). */
    id: z.string(),
    /** Acquisition time, UTC epoch seconds. The panel renders it in local time. */
    acquiredAt: z.number().int(),
    /** Fire radiative power (MW) — detection strength, if given. */
    frp: z.number().nullable(),
    /** "VIIRS NOAA-20" etc. */
    satellite: z.string(),
    /** "D" day / "N" night. */
    dayNight: z.enum(["D", "N"]),
    /** FIRMS confidence: VIIRS is low/nominal/high ("l"/"n"/"h"). */
    confidence: z.string().nullable(),
  }),
});
export type FireDetection = z.infer<typeof FireDetection>;

/**
 * Warning shown with every fire detection (web popup, mobile marker): a hotspot is satellite-detected
 * with ~2-3 h latency and clouds hide fire, so it must never read as live or as "area clear".
 * The UI word is in i18n ("fires.disclaimer"); this is the canonical Polish text for places that
 * build a plain label (the mobile marker takes a single string).
 */
export const FIRE_DISCLAIMER_PL =
  "Detekcja satelitarna, opóźnienie ~2–3 h, nie widzi przez chmury. Pomoc, nie wyrocznia.";

/** Confidence code (VIIRS: l/n/h) to a Polish word, or null when absent. */
export function fireConfidencePl(code: string | null): string | null {
  if (code === "l") return "niska";
  if (code === "n") return "nominalna";
  if (code === "h") return "wysoka";
  return null;
}

export const FireDetections = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(FireDetection),
  /** When the backend last fetched from FIRMS (UTC epoch seconds), for the "updated X ago" label. */
  fetchedAt: z.number().int(),
});
export type FireDetections = z.infer<typeof FireDetections>;
